import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Space,
  message,
  Row,
  Col,
  Statistic,
  Select,
  Divider,
  Input,
} from 'antd';
import {
  EyeOutlined,
  FileTextOutlined,
  ReloadOutlined,
  DollarOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';
import { downloadInvoicePdf } from '../utils/invoiceDownload';

const { Title } = Typography;
const { Option } = Select;

const OrderBilling = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [billingType, setBillingType] = useState('standard');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [creatingForOrder, setCreatingForOrder] = useState(null);
  const [orderStatus, setOrderStatus] = useState('all');
  const [searchText, setSearchText] = useState('');
  const queryClient = useQueryClient();

  // Fetch orders for billing
  const {
    data: orders = [],
    isLoading,
    refetch,
    isFetching,
  } = useQuery(
    ['orders-for-billing', orderStatus, searchText],
    async () => {
      const statusParam =
        orderStatus === 'all'
          ? 'pending,processing,shipped,delivered,completed,cancelled'
          : orderStatus;
      try {
        const params = { status: statusParam, per_page: 100 };
        if (searchText) {
          params.search = searchText;
        }
        const response = await api.get('/admin/billing/orders-for-billing', { params });
        const data = response.data?.data;
        // Handle paginated response
        if (data?.data) {
          return data.data;
        }
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Failed to fetch orders:', error);
        // Fallback to regular orders
        try {
          const fallback = await api.get('/admin/orders?per_page=100');
          return fallback.data?.data || fallback.data || [];
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
          return [];
        }
      }
    },
    { staleTime: 0, refetchOnWindowFocus: true },
  );

  // Generate bill mutation
  const generateBillMutation = useMutation(
    async ({ orderId, billingType }) => {
      const response = await api.post(`/admin/billing/orders/${orderId}/generate-bill`, {
        billing_type: billingType,
      });
      return response.data;
    },
    {
      onSuccess: (response) => {
        const invoice = response?.data;
        if (response?.already_exists && invoice) {
          const label = invoice.invoice_number || `#${invoice.id}`;
          message.info(`Invoice ${label} already exists for this order`, 5);
          downloadInvoicePdf(invoice, 'invoice');
        } else {
          message.success(response?.message || 'Bill created successfully!');
        }
        setIsCreateModalVisible(false);
        queryClient.invalidateQueries('orders-for-billing');
        queryClient.invalidateQueries('invoices');
      },
      onError: (error) => {
        message.error('Failed to create bill: ' + (error.response?.data?.message || error.message));
      },
    },
  );

  const handleViewDetails = async (order) => {
    try {
      const response = await api.get(`/admin/orders/${order.id}`);

      // Handle both wrapped and unwrapped responses
      let orderData = response.data?.data || response.data || order;

      // Ensure order_number exists (computed from id if not present)
      if (!orderData.order_number) {
        orderData.order_number = `ORD-${String(orderData.id).padStart(4, '0')}`;
      }
      setSelectedOrder(orderData);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      const fallbackOrder = { ...order };
      if (!fallbackOrder.order_number) {
        fallbackOrder.order_number = `ORD-${String(fallbackOrder.id).padStart(4, '0')}`;
      }
      setSelectedOrder(fallbackOrder);
    }
    setIsModalVisible(true);
  };

  const handleCreateBill = (order) => {
    setCreatingForOrder(order);
    setBillingType('standard');
    setIsCreateModalVisible(true);
  };

  const handleCreateBillSubmit = () => {
    if (creatingForOrder) {
      generateBillMutation.mutate({
        orderId: creatingForOrder.id,
        billingType,
      });
    }
  };

  // Helper to determine order type from items
  const getOrderType = (order) => {
    const items = order.items || [];
    const hasService = items.some(
      (i) => (i.product?.type || i.product?.type || i.type) === 'service',
    );
    return hasService ? 'service' : 'product';
  };

  // Calculate stats
  const totalOrders = orders.length;
  const productOrders = orders.filter((o) => getOrderType(o) === 'product').length;
  const serviceOrders = orders.filter((o) => getOrderType(o) === 'service').length;
  const totalValue = orders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id) => <span style={{ color: '#999' }}>#{id}</span>,
    },
    {
      title: 'Order #',
      key: 'order_number',
      render: (_, record) => (
        <strong>{record.order_number || `ORD-${String(record.id).padStart(4, '0')}`}</strong>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => {
        const customer = record.customer;
        return customer?.name || customer?.full_name || record.customer_name || 'N/A';
      },
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, record) => {
        const type = getOrderType(record);
        return <Tag color={type === 'product' ? 'blue' : 'green'}>{type.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      render: (amount) => `Rs. ${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: 'Delivery',
      dataIndex: 'delivery_charge',
      key: 'delivery_charge',
      render: (charge) => `Rs. ${parseFloat(charge || 0).toFixed(2)}`,
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => (
        <span style={{ fontWeight: '600', color: '#52c41a' }}>
          Rs. {parseFloat(amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'order_status',
      key: 'order_status',
      render: (status) => {
        const colorMap = {
          pending: 'orange',
          accepted: 'blue',
          preparing: 'purple',
          delivered: 'green',
          completed: 'green',
          cancelled: 'red',
        };
        return (
          <Tag color={colorMap[status] || 'default'}>{status?.replace('_', ' ').toUpperCase()}</Tag>
        );
      },
    },
    {
      title: 'Payment',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status) => {
        const colorMap = {
          pending: 'orange',
          paid: 'green',
          failed: 'red',
          refunded: 'red',
        };
        return <Tag color={colorMap[status] || 'default'}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<FilePdfOutlined />}
            onClick={() => handleCreateBill(record)}
          >
            Create Bill
          </Button>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
            View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <Title level={2} style={{ marginBottom: '24px', color: '#262626' }}>
        <FileTextOutlined /> Order Billing
      </Title>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
            <Statistic
              title="Total Orders"
              value={totalOrders}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
            <Statistic
              title="Product Orders"
              value={productOrders}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#3b82f6' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
            <Statistic
              title="Service Orders"
              value={serviceOrders}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#22c55e' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
            <Statistic
              title="Total Value"
              value={totalValue}
              precision={2}
              prefix="Rs. "
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}
        extra={
          <Space>
            <Input.Search
              placeholder="Search by customer name, email, phone..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(value) => setSearchText(value)}
              style={{ width: 280 }}
            />
            <Select
              value={orderStatus}
              onChange={(value) => setOrderStatus(value)}
              style={{ width: 150 }}
            >
              <Option value="all">All Orders</Option>
              <Option value="pending">Pending</Option>
              <Option value="processing">Processing</Option>
              <Option value="shipped">Shipped</Option>
              <Option value="delivered">Delivered</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={isLoading || isFetching}
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <FileTextOutlined
                  style={{ fontSize: '50px', color: '#ccc', marginBottom: '16px' }}
                />
                <p style={{ color: '#999' }}>No orders found. Try changing the status filter.</p>
              </div>
            ),
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Order Details Modal */}
      <Modal
        title={`Order Details - ${selectedOrder?.order_number}`}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedOrder(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsModalVisible(false);
              setSelectedOrder(null);
            }}
          >
            Close
          </Button>,
          <Button
            key="create"
            type="primary"
            icon={<FilePdfOutlined />}
            onClick={() => {
              setIsModalVisible(false);
              handleCreateBill(selectedOrder);
            }}
          >
            Create Bill
          </Button>,
        ]}
        width={800}
      >
        {selectedOrder && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small" title="Customer Information">
                  <p>
                    <strong>Name:</strong>{' '}
                    {selectedOrder.customer?.name ||
                      selectedOrder.customer?.full_name ||
                      selectedOrder.customer_name ||
                      'N/A'}
                  </p>
                  <p>
                    <strong>Email:</strong> {selectedOrder.customer?.email || 'N/A'}
                  </p>
                  <p>
                    <strong>Phone:</strong>{' '}
                    {selectedOrder.customer?.phone_number || selectedOrder.customer_phone || 'N/A'}
                  </p>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Order Information">
                  <p>
                    <strong>Order #:</strong> {selectedOrder.order_number}
                  </p>
                  <p>
                    <strong>Type:</strong>{' '}
                    <Tag
                      color={(() => {
                        const items = selectedOrder.items || [];
                        const hasService = items.some(
                          (i) =>
                            (i.product?.type || i.product?.type || i.type) ===
                            'service',
                        );
                        return hasService ? 'green' : 'blue';
                      })()}
                    >
                      {(() => {
                        const items = selectedOrder.items || [];
                        const hasService = items.some(
                          (i) =>
                            (i.product?.type || i.product?.type || i.type) ===
                            'service',
                        );
                        return hasService ? 'SERVICE' : 'PRODUCT';
                      })()}
                    </Tag>
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <Tag>{selectedOrder.order_status?.replace('_', ' ').toUpperCase()}</Tag>
                  </p>
                </Card>
              </Col>
            </Row>

            <Card size="small" title="Financial Breakdown" style={{ marginTop: 16 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="Subtotal"
                    value={parseFloat(selectedOrder.subtotal || 0)}
                    precision={2}
                    prefix="Rs. "
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Delivery"
                    value={parseFloat(selectedOrder.delivery_charge || 0)}
                    precision={2}
                    prefix="Rs. "
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Tax"
                    value={parseFloat(selectedOrder.tax || 0)}
                    precision={2}
                    prefix="Rs. "
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Total"
                    value={parseFloat(selectedOrder.total_amount || 0)}
                    precision={2}
                    prefix="Rs. "
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
              </Row>
            </Card>

            {selectedOrder.items && selectedOrder.items.length > 0 && (
              <Card size="small" title="Order Items" style={{ marginTop: 16 }}>
                <Table
                  dataSource={selectedOrder.items.map((item, i) => ({ ...item, key: i }))}
                  columns={[
                    {
                      title: 'Item',
                      key: 'name',
                      render: (_, record) =>
                        record.product?.name ||
                        record.product?.name ||
                        record.name ||
                        `ID: ${record.product_id}`,
                    },
                    {
                      title: 'Type',
                      key: 'type',
                      render: (_, record) => {
                        const type =
                          record.product?.type ||
                          record.product?.type ||
                          record.type;
                        return (
                          <Tag color={type === 'product' ? 'blue' : 'green'}>
                            {type?.toUpperCase() || 'N/A'}
                          </Tag>
                        );
                      },
                    },
                    { title: 'Qty', dataIndex: 'quantity', key: 'quantity' },
                    {
                      title: 'Unit Price',
                      dataIndex: 'price_at_order',
                      key: 'price',
                      render: (v) => `Rs. ${parseFloat(v || 0).toFixed(2)}`,
                    },
                    {
                      title: 'Total',
                      key: 'total',
                      render: (_, record) =>
                        `Rs. ${(parseFloat(record.quantity || 0) * parseFloat(record.price_at_order || 0)).toFixed(2)}`,
                    },
                  ]}
                  pagination={false}
                  size="small"
                />
              </Card>
            )}

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Card size="small" title="Payment Information">
                  <p>
                    <strong>Method:</strong>{' '}
                    {selectedOrder.payment_method?.replace('_', ' ').toUpperCase() || 'N/A'}
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <Tag color={selectedOrder.payment_status === 'paid' ? 'green' : 'orange'}>
                      {selectedOrder.payment_status?.toUpperCase()}
                    </Tag>
                  </p>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Dates">
                  <p>
                    <strong>Created:</strong>{' '}
                    {selectedOrder.created_at
                      ? dayjs(selectedOrder.created_at).format('MMM DD, YYYY HH:mm')
                      : 'N/A'}
                  </p>
                  <p>
                    <strong>Delivered:</strong>{' '}
                    {selectedOrder.delivered_at
                      ? dayjs(selectedOrder.delivered_at).format('MMM DD, YYYY')
                      : 'N/A'}
                  </p>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Create Bill Modal */}
      <Modal
        title={`Create Bill for Order #${creatingForOrder?.order_number || `ORD-${String(creatingForOrder?.id).padStart(4, '0')}`}`}
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          setCreatingForOrder(null);
        }}
        onOk={handleCreateBillSubmit}
        okText="Create Bill"
        confirmLoading={generateBillMutation.isLoading}
        width={500}
      >
        {creatingForOrder && (
          <div>
            <p style={{ marginBottom: 16 }}>
              <strong>Customer:</strong>{' '}
              {creatingForOrder.customer?.name ||
                creatingForOrder.customer?.full_name ||
                creatingForOrder.customer_name ||
                'N/A'}
            </p>
            <p style={{ marginBottom: 16 }}>
              <strong>Total Amount:</strong> Rs.{' '}
              {parseFloat(creatingForOrder.total_amount || 0).toFixed(2)}
            </p>

            <Divider>Select Billing Type</Divider>

            <div style={{ display: 'flex', gap: 16 }}>
              <Card
                hoverable
                style={{
                  flex: 1,
                  border: billingType === 'standard' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  borderRadius: 8,
                }}
                onClick={() => setBillingType('standard')}
              >
                <div style={{ textAlign: 'center' }}>
                  <FileTextOutlined
                    style={{ fontSize: 32, color: billingType === 'standard' ? '#1890ff' : '#999' }}
                  />
                  <h4 style={{ marginTop: 8 }}>Standard Billing</h4>
                  <p style={{ color: '#666', fontSize: 12 }}>No VAT</p>
                </div>
              </Card>

              <Card
                hoverable
                style={{
                  flex: 1,
                  border: billingType === 'vat' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  borderRadius: 8,
                }}
                onClick={() => setBillingType('vat')}
              >
                <div style={{ textAlign: 'center' }}>
                  <DollarOutlined
                    style={{ fontSize: 32, color: billingType === 'vat' ? '#1890ff' : '#999' }}
                  />
                  <h4 style={{ marginTop: 8 }}>VAT Billing</h4>
                  <p style={{ color: '#666', fontSize: 12 }}>13% VAT on products</p>
                </div>
              </Card>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrderBilling;
